import React, { useState, useEffect, useCallback, useRef } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import { useTranslation } from 'react-i18next';
import SchemaSet from "./Components/SchemaSet";
import Select from "react-select";
import Button from "../Tools/Components/Button";

const ValidatorPagina = ({ location }) => {
	const [dependencyGroup, setDependencyGroup] = useState(null);
	const [selectedSetName, setSelectedSetName] = useState("");
	const [selectedSetVersion, setSelectedSetVersion] = useState("production");
	const [versions, setVersions] = useState([]);
	const [input, setInput] = useState("");
	const [showResults, setShowResults] = useState(false);
	const [validationResults, setValidationResults] = useState({valid: false, errors: ["no results loaded yet"]});

	const { t } = useTranslation();

	const axiosRef = useRef(null);

	const handleSelectSet = useCallback(() => {
		if(selectedSetName === "") return;
		const axios = axiosRef.current;
		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "SchemaSet/all/" + dependencyGroup.name + "/" + selectedSetName)
			.then(response => setVersions(response.data.map(setVersion => { return { label: setVersion.version, value: setVersion.version }})))
			.catch(ex => console.log("an error occurred while fetching available versions: " + ex.message ));
	}, [dependencyGroup, selectedSetName]);

	useEffect(() => {
		handleSelectSet();
	}, [handleSelectSet, dependencyGroup, selectedSetName])

	const handleRunValidator = () => {
		const axios = axiosRef.current;
		axios.post(process.env.REACT_APP_SD_VALIDATOR_URL + "Validate/" + dependencyGroup.name + '/' + selectedSetName + '?version=' + selectedSetVersion, input, { headers: { "Content-Type": "text/plain" }})
		.then(response => {
			setValidationResults(response.data);
			setShowResults(true);
		})
		.catch((ex) => console.log("error while trying to connect to the validator: " + ex.message));
	};

	useEffect(() => {
		const httpService = HttpService;
		httpService.configure();
		axiosRef.current = httpService.getAxiosClient();

		if (location !== undefined && location.dependencyGroup) {
			setDependencyGroup(location.dependencyGroup);
			return;
		}
        
		const axios = axiosRef.current;
		if (!axios) return;
        const groupName = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "DependencyGroup/" + groupName)
			.then(response => {
				setDependencyGroup(response.data);
				if(response.data.dependants.length > 0) setSelectedSetName(response.data.dependants[0].name);
			})
			.catch(ex => { console.log("error while trying to load dependency group values: " + ex.message) });
	}, [location]);


	return (
		<div className="relative size-full">
			<div className="flex flex-row size-full gap-5">
				{/* MAIN CONTENT */}
				<div className="flex flex-col items-center size-full py-2">

					{/* HEADER */}
					<div className="bg-[#00aaad] dark:bg-[#008b8e] w-full flex justify-center py-[10px] border-2 border-[#b0b6c1]">
						<div className="font-bold text-[20px] py-3">
							{(dependencyGroup?.name ?? "") + " " + t("validator.header")}
						</div>
					</div>

					{/* ContentBox */}
					<div className="flex flex-col bg-[rgba(4, 5, 7, 0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border-2 border-[#b0b6c1]">

						<div className="flex flex-row justify-between items-center px-[28px] py-[10px] gap-[10px]">
							<Button text={t("validator.editSelectedSetBtn")} onClick={"/CreateValidatorSet/" + (dependencyGroup?.name ?? "") + "/" + selectedSetName}/>
							<label>{selectedSetName === "" ? "" : t("validator.selectedSetLabel") + " " + selectedSetName}</label>
							<div className="flex flex-row items-center gap-[5px]">
								<label>{t("validator.selectedVersionLabel")}</label>
								<Select
									options={versions}
                            		value={versions.find((ver) => ver.value === "production")}
                            		onChange={(option) => setSelectedSetVersion(option.value)}
								/>
							</div>
						</div>
						
						<div className="flex flex-row items-top px-[28px] py-[10px] gap-[10px]">
							{/* SchemaSet Table */}
							<div className="flex flex-col w-[30%]">
								<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] flex items-center px-[10px] font-bold border border-black">
									<div className="flex-1">{t("validator.schemaSetNameTableHeader")}</div>
									<div className="flex-1">{t("validator.schemaSetTopSchemaCount")}</div>
								</div>

								{(dependencyGroup?.dependants ?? []).map((schemaSet, index) => (
									<SchemaSet 
										key={schemaSet.name} 
										callback={() => {
											if(dependencyGroup.dependants.length <= index)
											{
												console.log("index '" + index + "' is out of bounds");
												console.log(dependencyGroup);
												return;
											}
											setSelectedSetName(dependencyGroup.dependants[index].name);
										}} 
										schemaSet={schemaSet} 
										index={index} 
									/>
								))}
								<div className="h-[35px] flex px-[10px]"/>
								<div className="flex items-center justify-center">
									<Button text={t("validator.createSetBtn") + ": " + (dependencyGroup?.name ?? "")} onClick={() => window.location.assign("/CreateValidatorSet/" + dependencyGroup.name)}/>
								</div>
							</div>
							{/*input text box*/}
							<div className="flex flex-col items-center bg-white dark:bg-gray-700 h-[90%] w-[70%] relative gap-[2%]">
								<div className="overflow-clip rounded-[inherit] size-full">
									<textarea
										id="validatorTextBox"
										className="h-[1000px] w-full p-[5px] bg-transparent text-black dark:text-white resize-none"
										placeholder={t("validator.inputPlaceholder")}
										onChange={event => setInput(event.target.value)}
									/>
								</div>
								<div className="absolute border border-black inset-0 pointer-events-none" />
								<Button text={t("validator.validateBtn")} onClick={() => handleRunValidator()}/>
							</div>
						</div>
					</div>
				
				</div>
			</div>
			{showResults ? 
			(
				<div className="flex flex-col items-center gap-[10px] fixed inset-0 justify-center bg-black/40 dark:bg-black/70"
					style={{
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 9999,
						padding: "20px",
						borderRadius: "8px",
						minWidth: "80%",
						minHeight: "80%"
					}}
					>
					<textarea className="bg-white text-black dark:bg-zinc-900 dark:text-white p-6 rounded-xl size-full resize-none"
						value={"input was " + (validationResults.valid ? "valid" : "invalid") + (validationResults.errors.length > 0 ? "\n\n - " + validationResults.errors.join("\n - ") : "")}
						readOnly
					/>
					<Button text={t("validator.closeFeedbackBtn")} onClick={() => setShowResults(false)}/>
				</div>
			) 
			: 
				("")
			}
		</div>
	);
};

export default ValidatorPagina;